import {
    forecastMultiples,
    specialMultiples,
    specialCombos,
} from './multiples'
import { calculateReturn } from './estimatedReturn'

export function isForecastMultiple(betType) {
    return !!forecastMultiples.find(name => name === betType)
}

function isSpecialMultiple(betType) {
    return !!Object.values(specialMultiples).find(special => special === betType)
}

function getDynamicComboLength(betType) {
    if (betType === 'double') {
        return 2
    }

    if (betType === 'treble') {
        return 3
    }

    // All other cases should be `accumulator-${n}`
    const num = betType.replace(/^\D+/g, '')

    return parseInt(num, 10)
}

function getBestPricesByEvent(selectedBets) {
    // Reduce selectedBets to 1 horse per race, picking the longest
    // possible price
    const races = selectedBets.map(selectedBet => (
        `${selectedBet.eventId}-${selectedBet.meetingId}`
    ))
    const uniqueRaces = Array.from(new Set(races))
    const bestPriced = uniqueRaces.map((hybridRaceId) => {
        const prices = selectedBets.filter(bet => (
            `${bet.eventId}-${bet.meetingId}` === hybridRaceId
        ))
        return prices.reduce((a, b) => (
            (a.decimalPrice >= b.decimalPrice) ? a : b
        ))
    })

    // best price per race
    return bestPriced.map(bet => (
        {
            hybridRaceId: `${bet.eventId}-${bet.meetingId}`,
            decimalPrice: bet.decimalPrice,
            fractionalPrice: bet.fractionalPrice,
            eachWayFraction: bet.eachWayFraction,
        }
    ))
}

/**
 * Generates all combinations of `comboLength` inside an array of bet objects.
 *
 * For example, it can fetch all doubles accross 8 horses, one component
 * of a goliath bet.
 *
 * @param  {Array<Object>} bets e.g. [{ a: 1 }, { b: 2 }, { c: 3 }]
 * @param  {Number} comboLength e.g. 2
 *
 * @return {Array} e.g. [
 *                          [{ a: 1 }, { b: 2 }],
 *                          [{ a: 2 }, { b: 3 }],
 *                          [{ a: 1 }, { b: 3 }],
 *                      ]
 */
function getBetCombinations(bets, comboLength) {
    const numberOfBets = bets.length
    let combinations = []

    const comboIsPossible = (i, end, currentIndex) => {
        const beforeEnd = i <= end
        const belowComboLimit = (end - i) + 1 >= (comboLength - currentIndex)

        return beforeEnd && belowComboLimit
    }

    const getCombination = (currentIndex, start, end, currentCombo) => {
        if (currentIndex === comboLength) {
            const combination = []
            for (let j = 0; j < comboLength; j += 1) {
                combination.push(currentCombo[j])
            }

            combinations.push(combination)
        }

        const newCombo = currentCombo.slice(0, currentCombo.length)
        for (let i = start; comboIsPossible(i, end, currentIndex); i += 1) {
            newCombo[currentIndex] = bets[i]
            // NOTE - recursion
            getCombination(currentIndex + 1, i + 1, end, newCombo)
        }
    }

    const currentCombo = [comboLength]
    const start = 0
    const end = numberOfBets - 1
    const startingIndex = 0
    getCombination(startingIndex, start, end, currentCombo)

    // Don't allow combinations where horses are in the same race
    combinations = combinations.filter((selections) => {
        const races = selections.map(selection => selection.hybridRaceId)
        const uniqueRaces = Array.from(new Set(races))

        return uniqueRaces.length === selections.length
    })

    return combinations
}

/**
 * Calculates the total of a [non-EW], straight multiple.
 * By "straight", we mean all odds are accumalated.
 *
 * @param  {Array} selectedBets
 * @param  {Number} parsedStake
 * @param  {Object} multiple
 *
 * @return {Number} e.g. 112.4
 */
function getReturnsForStraight(parsedStake, selectionsData) {
    let stake = parsedStake

    const returns = selectionsData.reduce((subTotal, selectionData) => {
        const {
            fractionalPrice,
        } = selectionData

        const betReturn = calculateReturn(
            stake,
            fractionalPrice,
            false,
        )
        stake = betReturn

        return betReturn
    }, 0)

    return returns
}

/**
 * Calculates the total of an EW straight multiple, such as a double.
 *
 * The business rule for the returns of a bet like this is:
 *   1. Accumalate the potential WIN returns, using each bet winnings as
 *      the stake for the next bet
 *   2. Accumalate the potential PLACE returns, using each bet winnings
 *      as the stake for the next bet
 *   3. Sum these 2 values together
 *
 * @param  {Array} selectedBets
 * @param  {Number} parsedStake
 * @param  {Object} multiple
 *
 * @return {Number} e.g. 112.4
 */
function getReturnsForStraightEw(parsedStake, selectionsData) {
    // 1. Accumalate win outcomes
    const accumalatedWinReturns = getReturnsForStraight(
        parsedStake,
        selectionsData,
    )

    // 2. Accumalate place outcomes
    let placeStake = parsedStake
    const accumalatedPlaceReturns = selectionsData.reduce((runningTotal, selectionData) => {
        const {
            fractionalPrice,
            eachWayFraction,
        } = selectionData

        const placeReturn = calculateReturn(
            placeStake,
            fractionalPrice,
            eachWayFraction,
            true,
        )

        placeStake = placeReturn

        return placeReturn
    }, 0)

    // 3. Sum the accumalated win returns & accumalated place returns
    return accumalatedWinReturns + accumalatedPlaceReturns
}

/**
 * Calculates the estimated returns for 1 multiple bet.
 * This could be a straight multiple like a double, a complex
 * multiple or even a forecast (forecast is always 0 as we show N/A)
 *
 * @param  {Array} selectedBets
 * @param  {Object} multiple - build bet data
 *
 * @return {Number} e.g. 112.4
 */
export function getReturnsForMultiple(selectedBets, multiple) {
    // Note that EW is at the multiple level here
    const {
        stakeValue,
        betType,
        isEachWay,
    } = multiple

    const parsedStake = Number.parseFloat(stakeValue) || 0

    // Do not include any forecast bets
    if (isForecastMultiple(betType)) {
        return 0
    }

    // Account for special multiples
    // e.g. trixie, lucky-15, goliath, etc
    if (isSpecialMultiple(betType)) {
        const multipleCombos = specialCombos[betType]
        const singlesPrices = selectedBets.map(bet => (
            {
                hybridRaceId: `${bet.eventId}-${bet.meetingId}`,
                decimalPrice: bet.decimalPrice,
                fractionalPrice: bet.fractionalPrice,
                eachWayFraction: bet.eachWayFraction,
            }
        ))

        const specialReturns = multipleCombos.reduce((returns, comboLength) => {
            const combos = getBetCombinations(singlesPrices, comboLength)
            combos.forEach((combo) => {
                const subReturns = (isEachWay)
                    ? getReturnsForStraightEw(parsedStake, combo)
                    : getReturnsForStraight(parsedStake, combo)
                returns.push(subReturns)
            })
            return returns
        }, [])

        const summedSpecialReturns = (specialReturns.length)
            ? specialReturns.reduce((a, b) => a + b)
            : 0

        return summedSpecialReturns
    }

    const comboLength = getDynamicComboLength(betType)
    const betPrices = getBestPricesByEvent(selectedBets)
    const combos = getBetCombinations(betPrices, comboLength)
    const dynamicReturns = combos.map((combo) => {
        const subReturns = (isEachWay)
            ? getReturnsForStraightEw(parsedStake, combo)
            : getReturnsForStraight(parsedStake, combo)

        return subReturns
    })

    const summedDynamicReturns = (dynamicReturns.length)
        ? dynamicReturns.reduce((a, b) => a + b)
        : 0

    return summedDynamicReturns
}

/**
 * Calculates the returns for all multiples (or a
 * subset, if that was to be required).
 *
 * @param  {Array} selectedBets
 * @param  {Array} multiples
 *
 * @return {Number} e.g. 112.4
 */
export function getReturnsForMultiples(selectedBets, multiples) {
    if (multiples.length === 0) {
        return 0
    }

    return multiples.reduce((runningTotal, multiple) => {
        const multipleReturns = getReturnsForMultiple(selectedBets, multiple)

        // Add together all totals of all multiples
        return runningTotal + multipleReturns
    }, 0)
}



// WEBPACK FOOTER //
// /src/js/common/utils/multiplesReturns.js